import { useState } from 'react'
import type { FieldRendererProps, FieldDisplayProps } from '../../config/types'
import { PartyPicker } from '@/components/shared/PartyPicker'
import { CreatePersonDialog } from '@/components/shared/CreatePersonDialog'
import { Text } from '@/components/ui/Text'

/**
 * Custom field — client/party picker (ENT-284 merged engagement-create flow).
 * Wraps the shared {@link PartyPicker} so the form-engine can collect a `party_id`
 * (the client director). Value is the selected party id string. The field label is
 * rendered by FieldWrapper, so PartyPicker's own label is intentionally omitted.
 *
 * When `field.props.allowCreatePerson === true`, an inline "Add new person" action
 * is surfaced in the picker footer. It opens the shared {@link CreatePersonDialog}
 * (name + email → `CreateOrGetNaturalPersonByEmail`, cache seeding), then sets the
 * form value to the new party id.
 */
export function PartyPickerField({ field, value, onChange, error, runtimeContext }: FieldRendererProps) {
  const allowCreate = field.props?.allowCreatePerson === true
  const actingTenantId = runtimeContext?.actingTenantId as string | undefined

  const excludePartyIds: string[] = []
  if (field.props?.excludeContextParty && runtimeContext?.partyId) {
    excludePartyIds.push(runtimeContext.partyId as string)
  }

  // null = closed; a string = open, seeded with the picker's search text.
  const [addPersonName, setAddPersonName] = useState<string | null>(null)

  return (
    <div className="space-y-1">
      <PartyPicker
        value={(value as string) || undefined}
        onChange={onChange}
        placeholder={field.placeholder ?? 'Select a party…'}
        personsOnly={field.props?.personsOnly === true}
        actingTenantId={actingTenantId}
        excludePartyIds={excludePartyIds}
        invalid={!!error}
        className="bg-surface-elevated"
        onCreatePerson={allowCreate ? (searchText) => setAddPersonName(searchText ?? '') : undefined}
      />
      {error && <Text variant="label-sm" color="danger">{error}</Text>}

      {allowCreate && addPersonName !== null && (
        <CreatePersonDialog
          open
          initialName={addPersonName}
          actingTenantId={actingTenantId}
          onClose={() => setAddPersonName(null)}
          onCreated={(person) => onChange(person.party_id)}
        />
      )}
    </div>
  )
}

/** Review-step display — shows the raw party id (labels aren't resolved here). */
export function PartyPickerDisplay({ value }: FieldDisplayProps) {
  return <Text variant="body-sm" as="span">{(value as string) || '—'}</Text>
}
